// Wormhole entity: every ~90 s a wormhole appears at a seeded random safe
// location in the arena. While active it drags enemies and the player into
// a swirl pool (radial pull + tangential current) — thrust escapes outside
// ~6 u, inside that only a flash breaks free — and player contact with the
// core triggers the wormhole bonus game (see bonus-game.ts). Deterministic:
// placement rolls come from a private mulberry32 stream derived from the run
// seed, and the pull is pure math — never Math.random or wall-clock
// (GDD Appendix A §A.1).

import type { WorldState } from './state.js';
import type { Vec2, WormholeState } from '../types.js';
import { cfg } from '../config.js';
import { wrappedDelta } from './collision.js';
import { RngStream } from './rng.js';

/** One wormhole every 90 s at 60 Hz. */
export const WORMHOLE_INTERVAL_TICKS = 90 * 60;
/** Contact radius of the wormhole vortex (arena units). */
export const WORMHOLE_RADIUS = 2.5;
/**
 * Pull field: enemies and the player inside this radius are dragged into
 * the swirl pool (arena units). Much wider than the visible vortex — the
 * current is felt well before the maw.
 */
export const WORMHOLE_PULL_RADIUS = 28;
/**
 * Accel profile: accel = min(MAX, STRENGTH / max(dist², MIN_DIST_SQ)).
 * Tuned against player.accel (64): thrust escapes outside ~6 u; inside the
 * inner zone the pull wins and only a flash (dash displacement ignores
 * velocity) breaks free before contact.
 */
const PULL_STRENGTH = 2400;
const PULL_MAX_ACCEL = 80;
const PULL_MIN_DIST_SQ = 9;
/** Fixed sim step; wormhole only runs on the 60 Hz fixed step. */
const DT = 1 / 60;

/** Radial pull acceleration at a given distance (exported for balance tests). */
export function wormholePullAccel(dist: number): number {
  return Math.min(PULL_MAX_ACCEL, PULL_STRENGTH / Math.max(dist * dist, PULL_MIN_DIST_SQ));
}
// Margin kept inside the ±100 arena walls for wormhole placement.
const ARENA_MARGIN = 4;
// Wormholes never materialize closer than this to the player.
const MIN_PLAYER_DIST = 30;
// Private stream salt: keeps wormhole placement rolls independent of the
// shared world streams (same pattern as UpgradeSystem's UPGRADE_STREAM_SALT),
// so spawn/loot/ai determinism is unaffected by wormhole timing.
const WORMHOLE_STREAM_SALT = 0x9E3779B9;

export function createWormholeState(): WormholeState {
  return {
    active: false,
    pos: { x: 0, y: 0 },
    radius: WORMHOLE_RADIUS,
    next_spawn_tick: WORMHOLE_INTERVAL_TICKS,
    collapse_tick: -1
  };
}

// The placement stream lives outside WorldState (like the director's WeakMap
// state) so WorldState stays plain data; reseeded whenever the run seed changes.
interface WormholeRng {
  seed: number;
  stream: RngStream;
}
const wormholeRng = new WeakMap<WorldState, WormholeRng>();

function getStream(world: WorldState): RngStream {
  let entry = wormholeRng.get(world);
  if (!entry || entry.seed !== world.runSeed) {
    entry = {
      seed: world.runSeed,
      stream: new RngStream((world.runSeed ^ WORMHOLE_STREAM_SALT) >>> 0)
    };
    wormholeRng.set(world, entry);
  }
  return entry.stream;
}

// Random arena location at least MIN_PLAYER_DIST (toroidal) from the player.
// Falls back to the last candidate if 16 tries all land too close (arena is
// 200 u wide, so this is effectively unreachable).
function pickSpawnPos(world: WorldState): Vec2 {
  const stream = getStream(world);
  const half = cfg.getNumber('sim.arena_size') / 2 - ARENA_MARGIN;
  const playerPos = world.player.pos;
  let fallback: Vec2 = { x: 0, y: 0 };
  for (let attempt = 0; attempt < 16; attempt++) {
    const pos = { x: stream.next_range(-half, half), y: stream.next_range(-half, half) };
    fallback = pos;
    const d = wrappedDelta(pos, playerPos);
    if (d.x * d.x + d.y * d.y >= MIN_PLAYER_DIST * MIN_PLAYER_DIST) return pos;
  }
  return fallback;
}

/**
 * Per-tick wormhole update. Spawns the wormhole when due, applies the pull
 * field (enemies + player) while active, and detects player contact.
 * Returns true on the tick the player enters — game.ts then starts the
 * bonus game. Inert while a bonus game is running or the run is over.
 */
export function updateWormhole(world: WorldState, tick: number): boolean {
  if (world.gameOver || !world.player.alive) return false;
  if (world.bonus.phase !== 'idle') return false;
  const wh = world.wormhole;

  if (!wh.active) {
    if (tick < wh.next_spawn_tick) return false;
    wh.active = true;
    wh.collapse_tick = -1;
    wh.pos = pickSpawnPos(world);
    world.events.emit(
      'spawn',
      { kind: 'wormhole', pos: { x: wh.pos.x, y: wh.pos.y }, radius: wh.radius },
      tick
    );
    return false;
  }

  // Pull field: enemies and the player inside the radius are dragged toward
  // the vortex; a tangential swirl rides on top so the caught orbit as they
  // drain (whirlpool, same profile as the black-hole wells in game.ts).
  // Deterministic: pure math, spin from the wormhole's position parity.
  const spin = (Math.floor(wh.pos.x) + Math.floor(wh.pos.y)) % 2 === 0 ? 1 : -1;
  const pullOne = (pos: Vec2, vel: Vec2): void => {
    const delta = wrappedDelta(wh.pos, pos); // direction pos → vortex
    const distSq = delta.x * delta.x + delta.y * delta.y;
    if (distSq > WORMHOLE_PULL_RADIUS * WORMHOLE_PULL_RADIUS || distSq < 1e-4) return;
    const dist = Math.sqrt(distSq);
    const force = wormholePullAccel(dist);
    vel.x += (delta.x / dist) * force * DT;
    vel.y += (delta.y / dist) * force * DT;
    // Swirl: strongest at mid-range — far out a gentle current, close in the
    // radial drain wins (black-hole whirlpool idiom).
    const mid = 1 - Math.abs(dist - WORMHOLE_PULL_RADIUS * 0.5) / (WORMHOLE_PULL_RADIUS * 0.5);
    const swirl = force * 1.1 * Math.max(0.25, mid);
    vel.x += (delta.y / dist) * swirl * spin * DT;
    vel.y += (-delta.x / dist) * swirl * spin * DT;
  };
  pullOne(world.player.pos, world.player.vel);
  for (const e of world.enemies.alive()) pullOne(e.pos, e.vel);

  const d = wrappedDelta(world.player.pos, wh.pos);
  const r = wh.radius + world.player.hitbox_radius;
  if (d.x * d.x + d.y * d.y > r * r) return false;
  // Player entered: consume the wormhole and schedule the next one. The
  // collapse tick drives the visual implosion (render/wormhole.ts) — the
  // vortex is gone from the sim immediately, it only LOOKS like it drains in.
  wh.active = false;
  wh.next_spawn_tick = tick + WORMHOLE_INTERVAL_TICKS;
  wh.collapse_tick = tick;
  world.events.emit(
    'phase_change',
    { system: 'wormhole', phase: 'entered', pos: { x: wh.pos.x, y: wh.pos.y } },
    tick
  );
  return true;
}
